import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import useAuth from '../hooks/useAuth';
import { normalizePreferences, getEnabledNotificationTypes } from '../features/settings/userPreferences';

/**
 * NotificationContext
 *
 * Provides real-time unread notification count and mark-all-as-read
 * sourced from the notifications table (see database/041_notifications_table.sql).
 *
 * Subscribes to INSERT events on the notifications table for the current user
 * so the bell badge updates in real time.
 */
const NotificationContext = createContext(null);

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [unseenCount, setUnseenCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [enabledTypes, setEnabledTypes] = useState(null); // null = not yet loaded

  useEffect(() => {
    if (!user) { setEnabledTypes(null); return; }
    let mounted = true;
    supabase
      .from('user_preferences')
      .select('preferences')
      .eq('user_id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (!mounted) return;
        const prefs = normalizePreferences(data?.preferences ?? {});
        setEnabledTypes(getEnabledNotificationTypes(prefs.notifications));
      });
    return () => { mounted = false; };
  }, [user]);

  const fetchUnseenCount = useCallback(async () => {
    if (!user) {
      setUnseenCount(0);
      setLoading(false);
      return;
    }

    // Fetch unread notification types so we can filter by user preferences client-side.
    const { data, error } = await supabase
      .from('notifications')
      .select('type')
      .eq('user_id', user.id)
      .eq('is_read', false);

    if (error) {
      console.error('Failed to fetch unseen notification count', error);
      setUnseenCount(0);
    } else {
      const types = enabledTypes; // null means prefs not yet loaded — count everything
      const count = types
        ? (data || []).filter((n) => types.includes(n.type)).length
        : (data || []).length;
      setUnseenCount(count);
    }
    setLoading(false);
  }, [user, enabledTypes]);

  useEffect(() => {
    if (!user) {
      setUnseenCount(0);
      setLoading(false);
      return;
    }

    fetchUnseenCount();

    // Subscribe to new notifications for this user via a filtered channel.
    // We use a raw channel with a Postgres changes filter on user_id.
    const channel = supabase
      .channel(`notifications_user_changes_${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          fetchUnseenCount();
        }
      )
      .subscribe();

    const intervalId = window.setInterval(() => {
      fetchUnseenCount();
    }, 30000);

    const onFocus = () => fetchUnseenCount();
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        fetchUnseenCount();
      }
    };

    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
      supabase.removeChannel(channel);
    };
  }, [user, fetchUnseenCount]);

  /**
   * markAllAsRead — calls the mark_all_notifications_read RPC
   * which sets is_read = true and read_at = now() for all unread
   * notifications belonging to the current user.
   */
  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    const { error } = await supabase
      .rpc('mark_all_notifications_read');

    if (!error) {
      setUnseenCount(0);
    }
  }, [user]);

  return (
    <NotificationContext.Provider
      value={{
        unseenCount,
        loading,
        markAllAsRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}

export default NotificationContext;